import React, { useMemo } from 'react';
import { RadioGroup } from 'src/components-dummy';
import { ApplyFieldDrift, DriftResolutionChoice } from 'src/services';
import { formatDriftSummaryLine, summarizeDriftByDomain } from './applyWinnerModal.utils';

export interface ApplyDriftSectionProps {
  drifted: ApplyFieldDrift[];
  winningVariantName: string;
  choice: DriftResolutionChoice;
  disabled?: boolean;
  onChoiceChange: (choice: DriftResolutionChoice) => void;
}

const OptionLabel = ({ title, helper }: { title: string; helper: string }): JSX.Element => (
  <div className='drift-choice-label'>
    <div className='drift-choice-title'>{title}</div>
    <div className='drift-choice-helper'>{helper}</div>
  </div>
);

export const ApplyDriftSection = ({
  drifted,
  winningVariantName,
  choice,
  disabled,
  onChoiceChange,
}: ApplyDriftSectionProps): JSX.Element => {
  const summaries = useMemo(() => summarizeDriftByDomain(drifted), [drifted]);
  const testedCount = drifted.filter(drift => drift.variantTestedThisField).length;

  return (
    <div className='apply-winner-modal-section'>
      <h4>Settings that drifted while the test ran</h4>
      <div className='drift-card'>
        <div className='drift-card-detail'>
          These settings were edited on your shop while the test ran, not by this test. Choose what
          happens to them.
        </div>
        <ul className='drift-summary-list'>
          {summaries.map(summary => (
            <li key={summary.domain}>{formatDriftSummaryLine(summary)}</li>
          ))}
        </ul>
        <RadioGroup
          name='drift-resolution-choice'
          selectedValue={choice}
          disabled={disabled}
          onChange={value => onChoiceChange(value as DriftResolutionChoice)}
          options={[
            {
              value: 'keep-live',
              label: (
                <OptionLabel
                  title='Keep all current values'
                  helper="Apply won't touch these settings; they stay as they are on your shop now."
                />
              ),
            },
            {
              value: 'restore-control',
              label: (
                <OptionLabel
                  title='Restore all control values'
                  helper='Reverts ALL listed edits back to what they were when the test started.'
                />
              ),
            },
          ]}
        />
        {testedCount > 0 && (
          <div className='drift-card-detail drift-tested-note'>
            {`${winningVariantName} also tested ${testedCount} of these setting(s), so for those the live value is not what the control froze. "Keep" leaves the live value; "Restore" uses the control value.`}
          </div>
        )}
      </div>
    </div>
  );
};
